import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

declare global {
    interface Window {
        openChatbot?: () => void;
        openChatBot?: () => void;
        chatbot?: { open?: () => void; show?: () => void; launch?: () => void };
        Chatbot?: { open?: () => void; show?: () => void; launch?: () => void };
        [key: string]: any;
    }
}

@Injectable({ providedIn: 'root' })
export class ChatbotService {
    private loadPromise?: Promise<void>;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: Object
    ) { }

    async open(): Promise<void> {
        if (!isPlatformBrowser(this.platformId)) return;

        await this.loadScriptsOnce();

        const openFn = await this.waitForOpenFunction();
        if (!openFn) {
            // Helpful diagnostics
            const keys = Object.keys(window).filter(k => /chat|bot/i.test(k)).sort();
            console.warn('No chatbot open function found. window keys:', keys);
            throw new Error('Chatbot function is not available on window (check demo.js globals / CSP).');
        }

        openFn();
    }

    private loadScriptsOnce(): Promise<void> {
        if (this.loadPromise) return this.loadPromise;

        this.loadPromise = (async () => {
            this.ensureContainer();            
           // await this.appendStylesheetOnce('https://chatbotva.dubaichamber.com/demo.css', 'chatbot-demo-css');
            await this.appendScriptOnce('https://chatbotva.dubaichamber.com/runtime.js', 'chatbot-runtime');
            await this.appendScriptOnce('https://chatbotva.dubaichamber.com/demo.js', 'chatbot-demo');
        })();

        return this.loadPromise;
    }

    private appendStylesheetOnce(href: string, id: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.document.getElementById(id)) return resolve();

            const link = this.document.createElement('link');
            link.id = id;
            link.rel = 'stylesheet';
            link.href = href;
            link.onload = () => resolve();
            link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
            this.document.head.appendChild(link);
        });
    }

    private ensureContainer(): void {
        // The vendor demo expects a host element to attach the widget to.
        const existing = this.document.getElementById('demo');
        if (!existing) {
            const host = this.document.createElement('div');
            host.id = 'demo';
            host.style.display = 'none';
            this.document.body.appendChild(host);
        }
    }

    private async waitForOpenFunction(timeoutMs = 5000, intervalMs = 150): Promise<(() => void) | null> {
        const deadline = Date.now() + timeoutMs;

        while (Date.now() < deadline) {
            const fn = this.findOpenFunction();
            if (fn) return fn;
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }

        return this.findOpenFunction();
    }

    private appendScriptOnce(src: string, id: string): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.document.getElementById(id)) return resolve();

            const s = this.document.createElement('script');
            s.id = id;
            s.src = src;
            s.type = 'text/javascript';
            s.async = false;

            s.onload = () => resolve();
            s.onerror = () => reject(new Error(`Failed to load script: ${src}`));

            this.document.body.appendChild(s);
        });
    }

    private findOpenFunction(): (() => void) | null {
        if (typeof window.openChatbot === 'function') return window.openChatbot;
        if (typeof window.openChatBot === 'function') return window.openChatBot;

        type ChatbotObj = { open?: () => void; show?: () => void; launch?: () => void };

        const objs: ChatbotObj[] = [];
        if (window.chatbot) objs.push(window.chatbot as ChatbotObj);
        if (window.Chatbot) objs.push(window.Chatbot as ChatbotObj);

        for (const obj of objs) {
            const open = obj.open;
            if (typeof open === 'function') return () => open();

            const show = obj.show;
            if (typeof show === 'function') return () => show();

            const launch = obj.launch;
            if (typeof launch === 'function') return () => launch();
        }

        const candidates = Object.keys(window).filter(
            (k) =>
                typeof (window as any)[k] === 'function' &&
                /open/i.test(k) &&
                /chat|bot/i.test(k)
        );

        if (candidates.length) {
            console.warn('Possible open function(s):', candidates);
            return (window as any)[candidates[0]] as () => void;
        }

        return null;
    }


}
